import {
  type ClaimsApi,
  ClaimsApiError,
  ClaimsConnectionError,
} from "@nmible-ltd/claims";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import i18n from "i18next";
import { useMemo } from "react";

import {
  API_V3_BASE_URL,
  getGetParticipantClaimTypesClaimTypesMeGetQueryOptions,
  getGetPdfPreviewReceiptPreviewGetQueryOptions,
  getGetVisitScheduleVisitsGetQueryOptions,
  useCalculateClaimReceiptCalculatePost,
  useCalculateMileageClaimMileageCalculatePost,
  useExtractReceiptReceiptExtractPost,
  useSubmitClaimReceiptPost,
  useSubmitMileageClaimMileagePost,
  useUploadPdfReceiptPdfPost,
  useUploadReceiptUploadPost,
} from "@/api-v3";
import StorageManager from "@/features/storage";
import { API_ERROR_CODES, parseApiError } from "@/lib/error-handler";

import { USE_MOCK_EXTRACTION } from "../helpers/extraction-mock";

const QUERY_STALE_TIME = 1000 * 60 * 5;
// useQuery's default, which fetchQuery does not share.
const QUERY_RETRY = 3;

// A 4xx answers the same however often it is asked.
const isClientError = (error: unknown) => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500;
};

// PMT-05 is answered in place by the claim flows, so the requests that can
// raise it opt out of the global toast for that code alone.
const inlineClaimErrors = {
  request: {
    inlineErrorCodes: [API_ERROR_CODES.PAYMENT_ACCOUNT_CANNOT_RECEIVE],
  },
};

// RCT-05 is a 403, which would otherwise sign the participant out.
const keepSignedIn = { request: { skipAuthErrorLogout: true } };

const toClaimsApiError = (error: unknown): never => {
  const parsed = parseApiError(error);
  if (parsed) throw new ClaimsApiError(parsed);
  if (isAxiosError(error) && !error.response) {
    throw new ClaimsConnectionError(error.message);
  }
  throw error;
};

export const usePwaClaimsApi = (): ClaimsApi => {
  const queryClient = useQueryClient();
  const { mutateAsync: calculateMileage } =
    useCalculateMileageClaimMileageCalculatePost(inlineClaimErrors);
  const { mutateAsync: submitMileage } =
    useSubmitMileageClaimMileagePost(inlineClaimErrors);
  const { mutateAsync: uploadReceipt } = useUploadReceiptUploadPost();
  const { mutateAsync: extractReceipt } =
    useExtractReceiptReceiptExtractPost(keepSignedIn);
  // The flow shows why a PDF was refused in place of the toast.
  const { mutateAsync: uploadPdfReceipt } = useUploadPdfReceiptPdfPost({
    request: { skipGlobalErrorToast: true },
  });
  const { mutateAsync: calculateReceipt } =
    useCalculateClaimReceiptCalculatePost(inlineClaimErrors);
  const { mutateAsync: submitReceipt } =
    useSubmitClaimReceiptPost(inlineClaimErrors);

  return useMemo(
    () => ({
      getVisitSchedule: async () => {
        const response = await queryClient.fetchQuery(
          getGetVisitScheduleVisitsGetQueryOptions(undefined, {
            query: { staleTime: QUERY_STALE_TIME, retry: QUERY_RETRY },
          })
        );
        return response.data;
      },
      calculateMileageClaim: (body) =>
        calculateMileage({ data: body }).catch(toClaimsApiError),
      submitMileageClaim: (body) =>
        submitMileage({ data: body }).catch(toClaimsApiError),
      getClaimTypes: async () => {
        const response = await queryClient.fetchQuery(
          getGetParticipantClaimTypesClaimTypesMeGetQueryOptions(undefined, {
            query: { staleTime: QUERY_STALE_TIME, retry: QUERY_RETRY },
          })
        );
        return response.data;
      },
      uploadReceipt: (file) =>
        uploadReceipt({ data: { file } }).catch(toClaimsApiError),
      extractReceipt: (file) =>
        extractReceipt({
          data: { file },
          params: USE_MOCK_EXTRACTION ? { mock: true } : undefined,
        }).catch(toClaimsApiError),
      // EventSource cannot send a bearer token, and the generated client
      // cannot stream, so the events are fetched by hand.
      openReceiptExtractionEvents: (extractionId, signal) => {
        const token = StorageManager.getTokenData()?.access_token;
        return fetch(
          `${API_V3_BASE_URL}/receipt/extract/${extractionId}/events`,
          {
            headers: {
              Accept: "text/event-stream",
              "Accept-Language": i18n.language.toLowerCase(),
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal,
          }
        );
      },
      calculateReceiptClaim: (body) =>
        calculateReceipt({ data: body }).catch(toClaimsApiError),
      submitReceiptClaim: (body) =>
        submitReceipt({ data: body }).catch(toClaimsApiError),
      pdfReceipts: {
        upload: (file) =>
          uploadPdfReceipt({ data: { file } }).catch(toClaimsApiError),
        // A COM-02 means the PDF is gone, which the flow answers by
        // forgetting it. Never an answer cached on an earlier visit, whose
        // links may have run out.
        getPreview: (pdfUrl) =>
          queryClient
            .fetchQuery(
              getGetPdfPreviewReceiptPreviewGetQueryOptions(
                { pdf_url: pdfUrl },
                {
                  query: {
                    gcTime: 0,
                    retry: (failureCount, error) =>
                      !isClientError(error) && failureCount < QUERY_RETRY,
                  },
                  request: { inlineErrorCodes: [API_ERROR_CODES.NOT_FOUND] },
                }
              )
            )
            .catch(toClaimsApiError),
        uploadPages: (pages) =>
          uploadReceipt({ data: pages }).catch(toClaimsApiError),
        extractPages: (pages) =>
          extractReceipt({
            data: pages,
            params: USE_MOCK_EXTRACTION ? { mock: true } : undefined,
          }).catch(toClaimsApiError),
      },
    }),
    [
      queryClient,
      calculateMileage,
      submitMileage,
      uploadReceipt,
      extractReceipt,
      uploadPdfReceipt,
      calculateReceipt,
      submitReceipt,
    ]
  );
};
